import Button from '@/components/Button/Button';
import SectionContainer from '@/components/SectionContainer/SectionContainer';
import { PATH } from '@/constants/path';
import { filterFaqsForContext } from '@/modules/JobListing/utils/filterFaqsForContext';
import { pushGtmEvent } from '@/utils/gtm';
import { buildFaqDescription, buildFaqHeading } from '@/utils/jobListingHelper';
import { stringifyJsonLd } from '@/utils/jsonLd';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { IFrequentlyAskedQuestion } from 'types/cms';

import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';

import styles from './FaqSection.module.scss';
import { FaqAccordionItem } from './components/FaqAccordionItem/FaqAccordionItem';
import { buildFaqPageJsonLd } from './faqSectionJsonLd';

export interface FaqSectionProps {
  faqs: IFrequentlyAskedQuestion[];
  grade?: string;
  speciality?: string;
  location?: string;
}

export const FaqSection = ({ faqs, grade, speciality, location }: FaqSectionProps) => {
  const filteredFaqs = filterFaqsForContext(faqs, { grade, speciality, location });
  const firstFaqId = filteredFaqs[0]?.id;
  const [openIds, setOpenIds] = useState<Set<number>>(
    () => new Set(firstFaqId !== undefined ? [firstFaqId] : []),
  );

  // Re-open the top-ranked FAQ whenever the context re-ranks the list (e.g. a client-side
  // navigation to a different grade/speciality/location keeps this section mounted), so the
  // "first question open" invariant survives navigations that don't remount the section.
  useEffect(() => {
    setOpenIds(new Set(firstFaqId !== undefined ? [firstFaqId] : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstFaqId]);

  if (!filteredFaqs.length) {
    return null;
  }

  const heading = buildFaqHeading({ grade, speciality, location });
  const description = buildFaqDescription({ grade, speciality, location });
  const faqJsonLd = buildFaqPageJsonLd(filteredFaqs);

  const handleToggle = (faq: IFrequentlyAskedQuestion) => {
    const isCurrentlyOpen = openIds.has(faq.id);

    setOpenIds(prev => {
      const next = new Set(prev);
      if (isCurrentlyOpen) {
        next.delete(faq.id);
      } else {
        next.add(faq.id);
      }
      return next;
    });

    pushGtmEvent({
      event: isCurrentlyOpen ? 'faq_collapse' : 'faq_expand',
      element: 'FAQ Question',
      faq_question: faq.question,
    });
  };

  const half = Math.ceil(filteredFaqs.length / 2);
  const columns = [filteredFaqs.slice(0, half), filteredFaqs.slice(half)];

  return (
    <div className={styles.faqSection} data-testid="job-listing-faq-section">
      {faqJsonLd && (
        <Head>
          <script
            key="faq-jsonld"
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: stringifyJsonLd(faqJsonLd) }}
          />
        </Head>
      )}
      <SectionContainer className={styles.faqSectionContainer}>
        <div className={styles.headerRow}>
          <div className={styles.titleBlock}>
            <h3>
              {heading.prefix} <span>{heading.accent}</span>
            </h3>
            <p>{description}</p>
          </div>
          <Button
            href={PATH.CONTACT_US}
            variant="outlined"
            className={styles.ctaButton}
            iconRight={<KeyboardArrowRight />}
            onClick={() =>
              pushGtmEvent({
                event: 'specialist_cta_click',
                element: 'Talk with a Solutions Specialist',
                event_label: 'faq_specialist_cta',
              })
            }
            trackFunction={null}
          >
            Talk with a Solutions Specialist
          </Button>
        </div>

        <div className={styles.grid}>
          {columns
            .filter(column => column.length > 0)
            .map((column, columnIndex) => (
              <div className={styles.column} key={columnIndex}>
                {column.map(faq => (
                  <FaqAccordionItem
                    key={faq.id}
                    data={faq}
                    open={openIds.has(faq.id)}
                    onToggle={() => handleToggle(faq)}
                  />
                ))}
              </div>
            ))}
        </div>
      </SectionContainer>
    </div>
  );
};

export default FaqSection;
